import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "@/lib/icons";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import {
  quizOf,
  slidesOf,
} from "@/routes/_authenticated/_workforce/training/-components/course-content";
import { SlidePanel } from "@/routes/_authenticated/_workforce/training/-components/slide-panel";

export const Route = createFileRoute(
  "/_authenticated/_workforce/training/courses/$courseId/preview",
)({
  component: PreviewCourse,
});

/** The course as staff meet it, recording nothing. Reads the preview projection so the server
 * strips the answer key. */
function PreviewCourse() {
  const { courseId } = Route.useParams();
  const [index, setIndex] = useState(0);
  const course = useQuery({
    ...orpc.workforce.training.previewCourse.queryOptions({ input: { courseId } }),
    retry: false,
  });

  if (course.isPending) return <Skeleton className="h-96 w-full" />;
  if (course.isError || !course.data) {
    return (
      <EmptyState
        title="This course could not be opened"
        description="It may have been removed, or it belongs to a building you cannot see."
        action={
          <Button variant="outline" asChild>
            <Link to="/training/courses">Back to courses</Link>
          </Button>
        }
      />
    );
  }

  const slides = slidesOf(course.data.slides);
  const quiz = quizOf(course.data.quiz);
  const slide = slides[index];

  return (
    <>
      {/* The editor's own back button, so the two screens leave the same way
          and the target is a control's height rather than a line of text. */}
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link params={{ courseId }} to="/training/courses/$courseId">
            <ArrowLeft />
            Back to the course
          </Link>
        </Button>
      </div>

      <PageHeader
        title={course.data.title}
        meta="A preview. Nothing here is recorded against anybody."
      />

      {slides.length === 0 ? (
        <EmptyState
          title="This course has no sections yet"
          description="Add one on the course itself and it will appear here."
        />
      ) : (
        <div className="space-y-4">
          {slide ? <SlidePanel slide={slide} /> : null}
          <div className="flex items-center justify-between">
            <Button
              disabled={index === 0}
              onClick={() => setIndex((current) => current - 1)}
              variant="outline"
            >
              Previous
            </Button>
            <p className="text-muted-foreground text-sm tabular-nums">
              Section {index + 1} of {slides.length}
            </p>
            <Button
              disabled={index >= slides.length - 1}
              onClick={() => setIndex((current) => current + 1)}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {quiz.enabled ? (
        <Card className="mt-6">
          <CardContent>
            <h2 className="font-semibold">Then a quiz</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              {quiz.questions.length} {quiz.questions.length === 1 ? "question" : "questions"}, and{" "}
              {course.data.passingScore}% is a pass.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}
